import { useState } from 'react';
import { useResendInvitation } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { useDialog } from '@spendbrain/common/hooks';
import { Invitee } from '@spendbrain/types/user';

interface UseResendInvitationDialogProps {
  onResendSuccess?: (invitee: Invitee) => void;
}

export const useResendInvitationDialog = ({
  onResendSuccess,
}: UseResendInvitationDialogProps = {}) => {
  const { handleClose, handleOpen, isOpen } = useDialog();
  const [inviteeToResend, setInviteeToResend] = useState<Invitee | null>(null);
  const { t } = useTranslation();
  const { open } = useNotification();
  const { mutateAsync: resendInvitation, isPending: loading } =
    useResendInvitation({
      onError: () =>
        open({
          type: 'error',
          message: t('components.shareDialog.resendInvitation.errorMessage'),
        }),
      onSuccess: () =>
        open({
          type: 'success',
          message: t('components.shareDialog.resendInvitation.successMessage'),
        }),
    });

  const onResendClick = (invitee: Invitee) => {
    setInviteeToResend(invitee);
    handleOpen();
  };

  const onConfirm = async () => {
    if (!inviteeToResend?.oid) return;
    await resendInvitation({ userId: inviteeToResend.oid });
    onResendSuccess?.(inviteeToResend);

    handleClose();
  };

  return {
    onClose: handleClose,
    onCancel: handleClose,
    onConfirm,
    open: isOpen,
    onOpenDialog: onResendClick,
    invitee: inviteeToResend,
    loading,
  };
};
